import PERMISSIONS from '@/constants/permissions';
import { fetchLeadMailThreads, fetchMyMailboxRequest } from '@/services/api/email';
import {
  MailMessage,
  MailThread,
  MailThreadsResult,
  MailThreadScope,
} from '@/typings/models/Email';
import { useHasAccess } from '@/utils/hooks';
import { F } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Button, Empty, Input, Segmented, Space, Tooltip } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'umi';
import styles from './EmailApp.less';
import EmailComposer, { ReplyContext } from './EmailComposer';
import EmailList, { threadKey } from './EmailList';
import EmailViewer from './EmailViewer';

const POLL_INTERVAL_MS = 3000;
/** Long enough that a typed word is one Graph sweep, not one per letter. */
const SEARCH_DEBOUNCE_MS = 450;
/** How long a just-sent message needs before Graph will return it in a search. */
const SEND_SETTLE_MS = 2500;

interface EmailAppProps {
  leadId: string;
  /** The lead's customer address — prefills new messages. */
  customerEmail?: string | null;
  isEmbedded?: boolean;
  /** Opened straight into the composer, e.g. from the lead card's + button. */
  initialCompose?: boolean;
  /** Pre-selected conversation, e.g. the row clicked on the lead card. */
  initialThread?: MailThread | null;
  onEmailSent?: (sentFromMailbox: string) => void;
}

/**
 * Lead email through the office365_mail integration. Everything is fetched
 * live from Microsoft Graph — Outlook is the single source of truth; nothing
 * shown here is stored in Conversional.
 *
 * The per-lead mailbox fan-out is deadline-bounded on the server: a response
 * with complete=false is partial, and this component polls until the sweep
 * finishes. Two panes inside a drawer-friendly layout (no antd Sider).
 */
const EmailApp: React.FC<EmailAppProps> = ({
  leadId,
  customerEmail,
  initialCompose,
  initialThread,
  onEmailSent,
}) => {
  const intl = useIntl();
  const [threads, setThreads] = useState<MailThread[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [incomplete, setIncomplete] = useState(false);
  const [pendingMailboxes, setPendingMailboxes] = useState(0);
  const [failedMailboxes, setFailedMailboxes] = useState<string[]>([]);
  const [notConnected, setNotConnected] = useState(false);
  const [selected, setSelected] = useState<MailThread | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [replyContext, setReplyContext] = useState<ReplyContext | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  /** The term the newest in-flight request asked for; older answers are dropped. */
  const appliedSearch = useRef('');
  const canCompose = useHasAccess([PERMISSIONS.MessageThreads.COMPOSE]);
  const canViewMail = useHasAccess([
    PERMISSIONS.MessageThreads.VIEW,
    PERMISSIONS.MessageThreads.VIEW_ALL,
  ]);
  // Client-side hint only: the server's can_view_all on the threads response
  // is authoritative for the All/Mine switch, because it also accounts for
  // feature flags. This just avoids one wasted render of the switch.
  const mayViewAll = useHasAccess([PERMISSIONS.MessageThreads.VIEW_ALL]);
  // Distinct from canCompose (a role permission): this is per-user Microsoft
  // identity. GET /mail/me/mailbox 409s exactly when no mailbox resolves for
  // this user, which is when SendAsResolver::resolveForNewMessage() throws.
  // Deliberately NOT read off /me: that endpoint is still served by CakePHP,
  // which has no access to SendAsResolver's three-tier resolution.
  //
  // It gates reading as well as sending — "Involving me" matches on the user's
  // resolved mailbox — so it is fetched for anyone who can view mail, not only
  // for users who can compose.
  const [mailboxUnavailable, setMailboxUnavailable] = useState(false);
  // Nothing that depends on the answer renders before it arrives: a control
  // shown and then withdrawn can strand a user in a scope they can no longer
  // switch out of.
  const [mailboxChecked, setMailboxChecked] = useState(false);
  // The user's chosen scope, always sent explicitly. A poll must never
  // change it: echoing the server's scope back into state let a background
  // refresh silently flip the switch the user had set.
  const [scope, setScope] = useState<MailThreadScope>('all');
  const [canViewAll, setCanViewAll] = useState(false);
  const [totalBeforeScope, setTotalBeforeScope] = useState(0);

  const load = useCallback(
    (
      rescan = false,
      requestedScope: MailThreadScope = 'all',
      requestedSearch = '',
    ) => {
      appliedSearch.current = requestedSearch;
      fetchLeadMailThreads(leadId, rescan, requestedScope, requestedSearch).then((APIResponse: any) => {
        processAPIResponseNow(
          APIResponse,
          {
            onSuccess(response: MailThreadsResult) {
              // Typing outruns the network: a slow answer for "inv" must not
              // overwrite the answer for "invoice". The server echoes the term
              // it searched, so anything but the current one is dropped.
              if ((response.search || '') !== appliedSearch.current) return;
              setThreads(response.threads || []);
              setIncomplete(!response.complete);
              setPendingMailboxes(response.pending_mailboxes || 0);
              setFailedMailboxes(response.failed_mailboxes || []);
              // Server is authoritative: a user without view_all is served
              // 'mine' whatever we asked for.
              setScope(response.scope);
              setCanViewAll(Boolean(response.can_view_all));
              setTotalBeforeScope(response.total_before_scope || 0);
              if (!response.complete) {
                pollTimer.current = setTimeout(
                  () => load(false, requestedScope, requestedSearch),
                  POLL_INTERVAL_MS,
                );
              }
            },
            onError(error: any) {
              // 409 — integration not connected for this partner.
              if (error?.status === 409 || error?.response?.status === 409) {
                setNotConnected(true);
              }
            },
            onEnd() {
              // A superseded answer must not drop the spinner either — the
              // request the user is actually waiting for is still in flight.
              if (requestedSearch === appliedSearch.current) setLoading(false);
            },
          },
          'data',
        );
      });
    },
    [leadId],
  );

  useEffect(() => {
    if (!canViewMail) {
      setLoading(false);
      return;
    }
    setLoading(true);
    load();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [load, canViewMail]);

  useEffect(() => {
    if (!canViewMail) return;
    fetchMyMailboxRequest().then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess() {
            setMailboxUnavailable(false);
          },
          onError() {
            setMailboxUnavailable(true);
          },
          onEnd() {
            setMailboxChecked(true);
          },
        },
        'data',
      );
    });
  }, [canViewMail]);

  // Search is answered by Outlook, not by filtering what is already on screen:
  // Graph matches message bodies and every folder, so it finds threads this
  // lead's sweep never returned. That costs a round trip per settled keystroke,
  // hence the debounce.
  useEffect(() => {
    if (!canViewMail) return undefined;
    const term = searchQuery.trim();
    if (term === appliedSearch.current) return undefined;
    const timer = setTimeout(() => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      setLoading(true);
      setSelected(null);
      setThreads([]);
      load(false, scope, term);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, scope, load, canViewMail]);

  // Clicking a row on the lead card means "open that conversation", so the
  // viewer starts on it instead of the pick-a-conversation placeholder. The
  // row carries the whole thread, and the viewer fetches by mailbox +
  // conversation id, so nothing waits on the list request.
  useEffect(() => {
    if (initialThread) setSelected(initialThread);
  }, [initialThread]);

  // Opened as "write a message", not "read the thread list": wait for the
  // mailbox check first, so a user without one gets the notice instead of a
  // composer that cannot send.
  useEffect(() => {
    if (initialCompose && canCompose && mailboxChecked && !mailboxUnavailable) {
      setComposerOpen(true);
    }
  }, [initialCompose, canCompose, mailboxChecked, mailboxUnavailable]);

  const handleSent = (sentFromMailbox: string) => {
    setReplyContext(null);
    // The sent message lands in the mailbox's Sent Items, but Graph does not
    // make it searchable the instant the send returns — refetching immediately
    // usually comes back without it, which reads as a failed send. Wait, then
    // refetch with rescan so the server's 3-minute cache can't hide it either.
    setTimeout(() => {
      setRefreshToken((token) => token + 1);
      load(true, scope, appliedSearch.current);
    }, SEND_SETTLE_MS);
    onEmailSent?.(sentFromMailbox);
  };

  const handleReply = (mailMessage: MailMessage, replyAll: boolean) => {
    if (!selected) return;
    setReplyContext({
      message: mailMessage,
      mailbox: selected.mailbox_address,
      replyAll,
    });
    setComposerOpen(true);
  };

  /** Mailbox captions on rows only earn their space when they disambiguate. */
  const showMailbox = useMemo(
    () => new Set(threads.map((thread) => thread.mailbox_address)).size > 1,
    [threads],
  );

  if (!canViewMail) {
    return (
      <div className={styles.notConnected}>
        <F
          id="email.no_permission"
          defaultMessage="You do not have permission to view email on projects."
        />
      </div>
    );
  }

  if (notConnected) {
    return (
      <div className={styles.notConnected}>
        <F
          id="email.not_connected"
          defaultMessage="Microsoft 365 is not connected. An administrator can install it under Integrations → Apps."
        />
      </div>
    );
  }

  return (
    <div className={styles.emailApp}>
      <div className={styles.listPane}>
        <div className={styles.listHeader}>
          <div className={styles.toolbar}>
            {canCompose && !mailboxUnavailable && (
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setReplyContext(null);
                  setComposerOpen(true);
                }}
              >
                <F id="email.action.new" defaultMessage="New email" />
              </Button>
            )}
            {/* Two conditions, both about the switch doing something real:
                the user must be able to see both scopes (without view_all the
                server forces 'mine' anyway), and they must have a mailbox —
                'Involving me' matches on the mailbox resolved for them, so
                without one it can only ever return their login address's mail,
                which is not what the label promises. */}
            {canViewAll && mayViewAll && mailboxChecked && !mailboxUnavailable && (
              <Segmented
                size="small"
                value={scope}
                onChange={(next) => {
                  setLoading(true);
                  setSelected(null);
                  // Cleared so EmailList's skeleton shows for the new scope
                  // instead of leaving the previous scope's rows on screen.
                  setThreads([]);
                  setScope(next as MailThreadScope);
                  load(false, next as MailThreadScope, appliedSearch.current);
                }}
                options={[
                  {
                    value: 'all',
                    label: intl.formatMessage({
                      id: 'email.scope.all',
                      defaultMessage: 'All',
                    }),
                  },
                  {
                    // No count here on purpose: while viewing 'all' the
                    // server hasn't computed the participant-filtered count,
                    // and a guessed number would be wrong.
                    value: 'mine',
                    label: intl.formatMessage({
                      id: 'email.scope.mine',
                      defaultMessage: 'Involving me',
                    }),
                  },
                ]}
              />
            )}
            <Tooltip
              title={
                <F
                  id="email.action.rescan"
                  defaultMessage="Re-scan mailboxes"
                />
              }
            >
              <Button
                className={styles.toolbarRight}
                size="small"
                icon={<ReloadOutlined />}
                // Icon-only: the Tooltip is hover-only, so it gives no
                // accessible name to screen readers or touch users.
                aria-label={intl.formatMessage({
                  id: 'email.action.rescan',
                  defaultMessage: 'Re-scan mailboxes',
                })}
                onClick={() => {
                  if (pollTimer.current) clearTimeout(pollTimer.current);
                  setLoading(true);
                  setThreads([]);
                  load(true, scope, appliedSearch.current);
                }}
              />
            </Tooltip>
          </div>
          {/* Only meaningful while scoped: says how much of the lead's mail
              the current filter is hiding, which is the one number a user in
              'mine' actually wants. */}
          {scope === 'mine' && canViewAll && totalBeforeScope > threads.length && (
            <div className={styles.scopeCaption}>
              <F
                id="email.scope.showing_subset"
                defaultMessage="Showing {shown} of {total} conversations on this project"
                values={{ shown: threads.length, total: totalBeforeScope }}
              />
            </div>
          )}
          {/* Shown to anyone who can read mail, not just to composers: without a
              mailbox the scope switch and Reply are both withdrawn, and this is
              the only thing on screen that says why. Visible rather than a
              tooltip — the controls it explains are absent, not disabled, so
              there is nothing left to hover. */}
          {mailboxUnavailable && (
            <Alert
              type="warning"
              showIcon
              className={styles.mailboxNotice}
              message={
                <F
                  id="email.mailbox_not_connected_hint"
                  defaultMessage="Your Outlook mailbox is not connected to your account. Contact your administrator for this change."
                />
              }
            />
          )}
          {/* Kept mounted once a term exists: a search that finds nothing must
              still leave the user a box to clear. */}
          {(threads.length > 0 || searchQuery !== '') && (
            <div className={styles.searchRow}>
              <Input
                allowClear
                size="small"
                prefix={<SearchOutlined className={styles.searchIcon} />}
                placeholder={intl.formatMessage({
                  id: 'email.list.search_placeholder',
                  defaultMessage: 'Search this project\u2019s mail in Outlook',
                })}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          )}
        </div>
        <EmailList
          threads={threads}
          loading={loading}
          incomplete={incomplete}
          pendingMailboxes={pendingMailboxes}
          failedMailboxes={failedMailboxes}
          showMailbox={showMailbox}
          emptyText={
            searchQuery.trim() ? (
              <F
                id="email.threads.no_search_results"
                defaultMessage="Outlook found no mail with this project matching your search"
              />
            ) : scope === 'mine' && totalBeforeScope > 0 ? (
              // Without this branch the list claimed "No email communication
              // yet" while the caption above it said "Showing 0 of N" — two
              // contradictory statements on screen, and no way out. Say why
              // it's empty and offer the way out.
              <Space direction="vertical" size="small">
                <F
                  id="email.threads.none_involving_me"
                  defaultMessage="None of the {total} conversations on this project involve you"
                  values={{ total: totalBeforeScope }}
                />
                <Button
                  size="small"
                  onClick={() => {
                    setLoading(true);
                    setThreads([]);
                    setScope('all');
                    load(false, 'all', appliedSearch.current);
                  }}
                >
                  <F id="email.threads.show_all" defaultMessage="Show all conversations" />
                </Button>
              </Space>
            ) : undefined
          }
          selectedKey={selected ? threadKey(selected) : null}
          onSelect={setSelected}
        />
      </div>
      <div className={styles.viewerPane}>
        {selected ? (
          <EmailViewer
            leadId={leadId}
            thread={selected}
            onReply={handleReply}
            canReply={canCompose && !mailboxUnavailable}
            refreshToken={refreshToken}
          />
        ) : (
          <div className={styles.viewerPlaceholder}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <F
                  id="email.viewer.pick_thread"
                  defaultMessage="Select a conversation"
                />
              }
            />
          </div>
        )}
      </div>
      <EmailComposer
        open={composerOpen}
        leadId={leadId}
        defaultTo={customerEmail}
        replyContext={replyContext}
        onClose={() => {
          setComposerOpen(false);
          setReplyContext(null);
        }}
        onSent={handleSent}
      />
    </div>
  );
};

export default EmailApp;
